import { Controller } from "@hotwired/stimulus"
import { escapeHtml as escapeHtmlValue } from "../helpers/escape_html"

const PREDEFINED_PERSONAS = [
  "Accountant",
  "Business Analyst",
  "C-level Executive",
  "Compliance Officer",
  "Consultant",
  "Customer Support",
  "Data Analyst",
  "Designer",
  "Educator",
  "Engineer",
  "Finance Manager",
  "HR Manager",
  "IT Manager",
  "Legal Counsel",
  "Marketing Manager",
  "Operations Manager",
  "Product Manager",
  "Project Manager",
  "Researcher",
  "Sales Manager"
]

export default class extends Controller {
  static targets = ["input", "tags", "suggestions"]

  connect() {
    this.selectedIndex = -1
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.selectedIndex >= 0 && this.hasSuggestionsTarget) {
        const items = this.suggestionsTarget.querySelectorAll("[data-suggestion]")
        if (items[this.selectedIndex]) {
          this.selectSuggestion(items[this.selectedIndex].dataset.suggestion)
          return
        }
      }
      this.addTag()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.navigateSuggestions(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.navigateSuggestions(-1)
    } else if (event.key === "Escape") {
      this.hideSuggestions()
    }
  }

  onInput() {
    const query = this.getCurrentQuery()
    if (query.length === 0) {
      this.hideSuggestions()
      return
    }
    this.showFilteredSuggestions(query)
  }

  getCurrentQuery() {
    const value = this.inputTarget.value
    // Get the text after the last comma (if any)
    const parts = value.split(",")
    return parts[parts.length - 1].trim()
  }

  showFilteredSuggestions(query) {
    if (!this.hasSuggestionsTarget) return

    const existingValues = this.getExistingTagValues()
    const filtered = PREDEFINED_PERSONAS.filter(p =>
      p.toLowerCase().includes(query.toLowerCase()) &&
      !existingValues.includes(p.toLowerCase())
    )

    if (filtered.length === 0) {
      this.hideSuggestions()
      return
    }

    this.selectedIndex = -1
    this.suggestionsTarget.innerHTML = filtered.map(persona =>
      `<div class="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded"
            data-suggestion="${this.escapeHtml(persona)}"
            data-action="mousedown->tag-input-guideline#onSuggestionClick">
        ${this.escapeHtml(persona)}
      </div>`
    ).join("")

    this.suggestionsTarget.classList.remove("hidden")
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add("hidden")
      this.selectedIndex = -1
    }
  }

  navigateSuggestions(direction) {
    if (!this.hasSuggestionsTarget) return
    const items = this.suggestionsTarget.querySelectorAll("[data-suggestion]")
    if (items.length === 0) return

    // Remove previous highlight
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove("bg-gray-100")
    }

    this.selectedIndex += direction
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1
    if (this.selectedIndex >= items.length) this.selectedIndex = 0

    items[this.selectedIndex].classList.add("bg-gray-100")
    items[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }

  onSuggestionClick(event) {
    event.preventDefault()
    const persona = event.currentTarget.dataset.suggestion
    this.selectSuggestion(persona)
  }

  selectSuggestion(persona) {
    // If there's comma-separated text before, keep those parts and replace only the current query
    const value = this.inputTarget.value
    const parts = value.split(",")
    parts.pop() // remove the current partial query

    if (parts.length > 0) {
      // Add remaining comma-separated values first
      parts.forEach(part => {
        const trimmed = part.trim()
        if (trimmed) this.addSingleTag(trimmed)
      })
    }

    this.addSingleTag(persona)
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.hideSuggestions()
  }

  addTag() {
    const value = this.inputTarget.value
    if (value.trim() === "") return

    // Split by commas and add each as separate tag
    const values = value.split(",").map(v => v.trim()).filter(v => v !== "")
    values.forEach(v => this.addSingleTag(v))

    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.hideSuggestions()
  }

  addSingleTag(value) {
    if (value === "") return

    // Check for duplicates
    const existingValues = this.getExistingTagValues()
    if (existingValues.includes(value.toLowerCase())) return

    // Create tag element
    const tagHTML = `
      <span class="inline-flex items-center gap-1 px-2 py-1 bg-[#F2F3F5] rounded-full text-xs font-medium text-[#374151]"
            data-tag-value="${this.escapeHtml(value)}">
        ${this.escapeHtml(value)}
        <button type="button" class="hover:text-[#B42318]" data-action="click->tag-input-guideline#removeTag">
          <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6L18 18"/>
          </svg>
        </button>
      </span>
    `

    this.tagsTarget.insertAdjacentHTML("beforeend", tagHTML)
  }

  getExistingTagValues() {
    const existingTags = this.tagsTarget.querySelectorAll("[data-tag-value]")
    return Array.from(existingTags).map(t => t.dataset.tagValue.toLowerCase())
  }

  removeTag(event) {
    const tag = event.currentTarget.closest("[data-tag-value]")
    if (tag) {
      tag.remove()
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  escapeHtml(text) {
    return escapeHtmlValue(text)
  }
}
